"use client";

import { parseAsStringLiteral, useQueryState } from "nuqs";
import { useEffect } from "react";
import { create } from "zustand";
import { usePOIView } from "@/features/items/hooks/usePOIView";

export const MOBILE_VIEWS = ["map", "list"] as const;
export type MobileView = (typeof MOBILE_VIEWS)[number];

interface PreviousViewStore {
  /**
   * The view showing when the POI panel opened, or null if nothing has been
   * recorded — which is the case when the app *opens* on a POI, following a
   * shared link.
   */
  previousView: MobileView | null;
  setPreviousView: (view: MobileView) => void;
}

export const usePreviousViewStore = create<PreviousViewStore>((set) => ({
  previousView: null,
  setPreviousView: (view) => set({ previousView: view }),
}));

/**
 * Which of the two mobile views is showing, and what to go back to.
 *
 * The view is a URL parameter rather than component state, unlike the legacy app.
 * On a phone the list and the map are separate screens, so this is genuinely
 * *where you are* — putting it in the URL makes the browser's back button do the
 * obvious thing and makes a shared link reproduce the screen the sender was on.
 *
 * `previousView` is still a store: it records where the user was when the POI panel
 * opened, so the panel's own back button returns there. That's a fact about history
 * rather than about the current location, and duplicating it into the URL would let
 * the two disagree after a back navigation.
 */
export function useMobileView() {
  const [view, setView] = useQueryState(
    "view",
    parseAsStringLiteral(MOBILE_VIEWS).withDefault("map")
  );
  const { isPOIView } = usePOIView();
  const stored = usePreviousViewStore((state) => state.previousView);
  const setPreviousView = usePreviousViewStore((state) => state.setPreviousView);

  // Falls back to the current view rather than a hardcoded "map" for the render
  // before the effect below has recorded anything. Opening the app straight onto a
  // POI — following a shared link — records nothing, and the legacy default sent
  // the back button to the map even when the link said list.
  const previousView = stored ?? view;

  useEffect(() => {
    // While the panel is closed, track the current view — so it's already frozen
    // at the right value by the time the panel opens. Recording on the way in
    // instead would need transition detection, and recording while open would
    // overwrite it with whatever the user navigated to behind the panel.
    if (!isPOIView) {
      setPreviousView(view);
      return;
    }

    // The app opened straight onto a POI, so nothing was ever recorded. Commit the
    // view from the link once, rather than leaving it to the live fallback above —
    // that would keep following the view and lose the freeze.
    if (stored === null) setPreviousView(view);
  }, [isPOIView, view, stored, setPreviousView]);

  return { view, setView, previousView, isPOIView };
}
